import bankList from "../../util/bankList";
import { useEffect, useState, useRef } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import { Formik, FieldArray } from "formik";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  useMediaQuery,
  Autocomplete,
  CircularProgress
} from "@mui/material";

/* ---------- API ---------- */
const token = localStorage.getItem("token");

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 15000,
  headers: { Authorization: `Bearer ${token}` },
});

/* ---------- API HELPERS ---------- */
const getUsers = () =>
  api.get("/api/user", { params: { _t: Date.now() } }).then((r) =>
    (r.data?.data || []).filter(
      (u) => String(u?.role?.name || "").toLowerCase() === "sales"
    )
  );

const getWarehouses = () =>
  api.get("/api/warehouse", { params: { _t: Date.now() } })
    .then((r) => r.data?.data || []);

const getStoreItems = (warehouseId) =>
  api.get("/api/store", {
    params: { warehouseId, _t: Date.now() },
  }).then((r) => r.data?.data || []);

/* ---------- COMPONENT ---------- */
export default function CreateSales() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);

  const mounted = useRef(true);

  const loggedUser = JSON.parse(localStorage.getItem("user")) || {};
  const roleName = loggedUser?.roleName || "";
  const isSalesUser = roleName.toLowerCase().includes("sales");
  const userId = loggedUser?.id || null;

  const isNonMobile = useMediaQuery("(min-width:600px)");

  /* ---------- LOAD CUSTOMERS ---------- */
  const loadCustomers = async (selectedUserId) => {
    if (!selectedUserId) return setCustomers([]);

    try {
      const res = await api.get(`/api/customer/user/${selectedUserId}`, {
        params: { _t: Date.now() },
      });

      setCustomers(
        Array.isArray(res.data?.data)
          ? res.data.data.filter((c) => c?.type === "customer")
          : []
      );
    } catch {
      setCustomers([]);
    }
  };

  /* ---------- LOAD ITEMS ---------- */
  const loadWarehouseItems = async (warehouseId) => {
    if (!warehouseId) return setItems([]);

    try {
      const data = await getStoreItems(warehouseId);

      const mapped = data.map((s) => ({
        id: s.itemId,
        name: s.item?.name || `#${s.itemId}`,
        unitPrice: s.item?.salePrice ?? 0,
        quantity: s.quantity,
      }));

      setItems(mapped);
    } catch {
      setToast({ open: true, msg: "Failed to load items", sev: "error" });
    }
  };

  /* ---------- LOAD OPTIONS ---------- */
  const loadOptions = async () => {
    setLoading(true);
    try {
      const [u, w] = await Promise.all([getUsers(), getWarehouses()]);
      if (!mounted.current) return;

      setUsers(u);
      setWarehouses(w);

      if (isSalesUser && userId) {
        await loadCustomers(userId);
      }
    } catch {
      setToast({ open: true, msg: "Failed to load options", sev: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    mounted.current = true;
    loadOptions();
    return () => (mounted.current = false);
  }, []);

  /* ---------- VALIDATION ---------- */
  const SCHEMA = yup.object({
    userId: isSalesUser
      ? yup.number()
      : yup.number().required("User required"),

    customerId: yup.number().nullable(),

    warehouseId: yup.number().required("Warehouse required"),

    salesDate: yup.string().required("Date required"),

    description: yup.string(),

    items: yup
      .array()
      .of(
        yup.object({
          itemId: yup.number().required(),
          quantity: yup.number().required().min(1),
          unitPrice: yup.number().required(),
        })
      )
      .min(1),
  });

  /* ---------- INITIAL ---------- */
  const INITIALS = {
    userId: isSalesUser ? userId : "",
    customerId: "",
    warehouseId: "",
    salesDate: new Date().toISOString().slice(0, 10), // YYYY-MM-DD
    description: "",
    items: [
      {
        itemId: "",
        unitPrice: 0,
        quantity: 1,
      },
    ],
  };

  /* ---------- SUBMIT ---------- */
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const itemsToSend = values.items.map((i) => ({
        itemId: Number(i.itemId),
        quantity: Number(i.quantity),
        unitPrice: Number(i.unitPrice),
      }));

      const payload = {
        userId: isSalesUser ? Number(userId) : Number(values.userId),
        customerId: values.customerId ? Number(values.customerId) : null,
        warehouseId: Number(values.warehouseId),
        status: "ready",
        salesDate: values.salesDate, // IMPORTANT: keep YYYY-MM-DD
        description: values.description || "",
        items: itemsToSend,
      };

      const res = await api.post("/api/sales", payload);

      if (!res || res.status >= 400) {
        setToast({
          open: true,
          msg: res?.data?.message || "Submit failed",
          sev: "error",
        });
        return;
      }

      setToast({ open: true, msg: "Sale Created!", sev: "success" });
      resetForm();
      loadOptions();
    } catch (err) {
      setToast({
        open: true,
        msg: err?.response?.data?.message || "Error",
        sev: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box m="20px">
      <Header title="CREATE SALE" subtitle="New sales order" />

      <Formik
        initialValues={INITIALS}
        validationSchema={SCHEMA}
        onSubmit={handleSubmit}
      >
        {(bag) => {
          

          return (
            <form onSubmit={bag.handleSubmit}>
              <Box display="grid" gap="20px" gridTemplateColumns="repeat(4,1fr)">

                {!isSalesUser && (
                  <TextField
                    select
                    label="User"
                    name="userId"
                    value={bag.values.userId}
                    onChange={async (e) => {
                      bag.handleChange(e);
                      await loadCustomers(e.target.value);
                    }}
                    variant="filled"
                  >
                    {users.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.fullName || u.username}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                <Autocomplete
                  options={customers}
                  getOptionLabel={(o) => o.name || ""}
                  onChange={(e, v) =>
                    bag.setFieldValue("customerId", v?.id || "")
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Customer" variant="filled" />
                  )}
                />

                <TextField
                  select
                  label="Warehouse"
                  name="warehouseId"
                  value={bag.values.warehouseId}
                  onChange={(e) => {
                    bag.handleChange(e);
                    loadWarehouseItems(e.target.value);
                  }}
                  variant="filled"
                >
                  {warehouses.map((w) => (
                    <MenuItem key={w.id} value={w.id}>
                      {w.name}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  type="date"
                  name="salesDate"
                  value={bag.values.salesDate}
                  onChange={bag.handleChange}
                  variant="filled"
                />

                <TextField
                  label="Description"
                  name="description"
                  value={bag.values.description}
                  onChange={bag.handleChange}
                  variant="filled"
                  fullWidth
                />
              </Box>

              {/* ---------- ITEMS ---------- */}
              <FieldArray name="items">
                {({ push, remove }) => (
                  <Box mt={2}>
                    {bag.values.items.map((item, idx) => {
                      const selectedItem = items.find(
                        (i) => String(i.id) === String(item.itemId)
                      );

                      return (
                        <Box
                          key={idx}
                          display="grid"
                          gridTemplateColumns="repeat(4,1fr)"
                          gap="10px"
                            
                        mt={2}
                        mb={1}
                        
                        >
                          <TextField
                            select
                            label="Item"
                            name={`items[${idx}].itemId`}
                            value={item.itemId}
                            onChange={bag.handleChange}
                           
                            variant="filled"
                          >
                            {items.map((i) => (
                              <MenuItem key={i.id} value={i.id}>
                                {i.name} 
                              </MenuItem>
                            ))}
                          </TextField>
                           <TextField
                            type="number"
                            label="Qty"
                            name={`items[${idx}].quantity`}
                            value={item.quantity}
                            onChange={bag.handleChange}
                            variant="filled"
                          />

                         <TextField
                            type="number"
                            label="Unit Price"
                            name={`items[${idx}].unitPrice`}
                            value={item.unitPrice}
                            onChange={bag.handleChange}
                            variant="filled"
                            />

                          
                          <Button color="error" onClick={() => remove(idx)}>
                            Remove
                          </Button>
                        </Box>
                      );
                    })}

                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() =>
                        push({
                          itemId: "",
                          unitPrice: 0,
                          quantity: 1,
                        
                        })
                      }
                    >
                      + Add Item
                    </Button>
                  </Box>
                )}
              </FieldArray>

         
 <Box display="flex" justifyContent="end" mt={3} gap={2}>
                <Button onClick={() => bag.resetForm()} variant="outlined">
                  Reset
                </Button>
                <Box position="relative" display="inline-flex">
                  <Button
                    type="submit"
                    color="secondary"
                    variant="contained"
                    disabled={bag.isSubmitting} // disable while loading
                  >
                    Create Sale
                  </Button>

                  {bag.isSubmitting && (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: "primary.main",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        marginTop: "-12px", // half of size
                        marginLeft: "-12px",
                      }}
                    />
                  )}
                </Box>
              </Box>
            </form>
          );
        }}
      </Formik>

      {/* ---------- TOAST ---------- */}
     <Snackbar
             anchorOrigin={{ vertical: "top", horizontal: "right" }}
             sx={{ mt: 5 }}
             open={toast.open}
             autoHideDuration={4000}
             onClose={() => setToast((s) => ({ ...s, open: false }))}
           >
             <Alert
               severity={toast.sev}
               variant="filled"
               onClose={() => setToast((s) => ({ ...s, open: false }))}
             >
               {toast.msg}
             </Alert>
           </Snackbar>
    </Box>
  );
}